import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TARGET = 'a, button, [role="button"], [role="tab"], [data-tip]';
const DELAY = 350;
const GAP = 10;
const EDGE = 8;

/**
 * What a control's tooltip says. Anything can ask for one with `data-tip`. Icon-only buttons get
 * theirs from the label they already have for screen readers, so new ones are covered by default.
 */
function tipFor(el: HTMLElement) {
  if (el.closest('.group\\/tip')) return null; // Has its own HoverTip.
  if (el.dataset.tip) return el.dataset.tip;
  const label = el.getAttribute('aria-label') || el.getAttribute('title');
  if (!label) return null;
  if (el.title) return label;
  const iconOnly = !el.textContent?.trim() && !el.querySelector('img');
  return iconOnly ? label : null;
}

interface Shown {
  text: string;
  rect: DOMRect;
}

/** The hover labels across the app, drawn over everything so scrolling rows can't clip them. */
export function HoverTips() {
  const [shown, setShown] = useState<Shown | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!window.matchMedia('(hover: hover)').matches) return;
    let current: HTMLElement | null = null;
    let timer: number | undefined;

    const hide = () => {
      window.clearTimeout(timer);
      // The browser's own tooltip would show as well, so a `title` steps aside while hovered.
      if (current?.dataset.tipTitle) {
        current.title = current.dataset.tipTitle;
        delete current.dataset.tipTitle;
      }
      current = null;
      setShown(null);
    };

    const show = (el: HTMLElement, delay: number) => {
      const text = tipFor(el);
      if (el === current) return;
      hide();
      if (!text || document.fullscreenElement) return;
      current = el;
      if (el.title) {
        el.dataset.tipTitle = el.title;
        el.removeAttribute('title');
      }
      timer = window.setTimeout(() => {
        // A menu of its own open, or gone from the page while waiting.
        if (!el.isConnected || el.dataset.state === 'open') return;
        setShown({ text, rect: el.getBoundingClientRect() });
      }, delay);
    };

    const over = (e: MouseEvent) => {
      const el = (e.target as Element | null)?.closest?.<HTMLElement>(TARGET);
      if (el) show(el, DELAY);
      else if (current) hide();
    };
    const focus = (e: FocusEvent) => {
      const el = e.target as HTMLElement;
      if (el.matches?.(TARGET) && el.matches(':focus-visible')) show(el, 0);
    };

    document.addEventListener('mouseover', over);
    document.addEventListener('focusin', focus);
    document.addEventListener('focusout', hide);
    document.addEventListener('mousedown', hide, true);
    document.addEventListener('keydown', hide, true);
    window.addEventListener('scroll', hide, { capture: true, passive: true });
    window.addEventListener('blur', hide);
    return () => {
      hide();
      document.removeEventListener('mouseover', over);
      document.removeEventListener('focusin', focus);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('mousedown', hide, true);
      document.removeEventListener('keydown', hide, true);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('blur', hide);
    };
  }, []);

  // Above the control, or below it when there's no room, and always inside the window.
  useLayoutEffect(() => {
    const el = box.current;
    if (!el || !shown) return;
    const { width, height } = el.getBoundingClientRect();
    const { rect } = shown;
    const top = rect.top - height - GAP < EDGE ? rect.bottom + GAP : rect.top - height - GAP;
    const left = Math.min(
      Math.max(EDGE, rect.left + rect.width / 2 - width / 2),
      window.innerWidth - width - EDGE,
    );
    el.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`;
    el.style.opacity = '1';
  }, [shown]);

  if (!shown) return null;
  return createPortal(
    <div
      ref={box}
      role="tooltip"
      className="pointer-events-none fixed left-0 top-0 z-[90] max-w-xs rounded-lg border border-white/10 bg-elevated px-2.5 py-1.5 text-xs font-semibold text-text opacity-0 shadow-xl shadow-black/50 transition-opacity duration-150"
    >
      {shown.text}
    </div>,
    document.body,
  );
}
